import React from "react";
import DataGrid, { Column, FilterRow, LoadPanel, Scrolling, Sorting } from "devextreme-react/data-grid";
import { EmailTemplateSummaryDto } from "../../../dtos/EmailTemplateSummaryDto";
import { EmailTemplateUsageDto } from "../../../dtos/EmailTemplateUsageDto";
import EmailTemplateViewerDialog from "../../tasklist/tabs/EmailTemplateViewerDialog";
import EmailTemplatesTreeView from "./EmailTemplatesTreeView";
import { ViewMode, UsageTreeNode } from "../hooks/useEmailTemplatesController";

type Props = {
    templates: EmailTemplateSummaryDto[];
    treeNodes: UsageTreeNode[];
    loading: boolean;
    selectedTemplateName: string | null;
    dialogVisible: boolean;
    viewMode: ViewMode;
    onViewModeChange: (mode: ViewMode) => void;
    onRowClick: (templateName: string) => void;
    onDialogHiding: () => void;
};

function formatUsages(usages: EmailTemplateUsageDto[]): string {
    if (!usages || usages.length === 0) return "(unused)";
    return usages.map(u => {
        let path = `Module: ${u.moduleName} > Task: ${u.taskName}`;
        if (u.subtaskName) path += ` > Subtask: ${u.subtaskName}`;
        return `${path} (${u.trigger})`;
    }).join("; ");
}

function nameCellRender(data: { data: EmailTemplateSummaryDto }) {
    const row = data.data;
    return (
        <div style={{ lineHeight: "1.4" }}>
            <div><strong>{row.name}</strong></div>
            <div className="text-muted">Type: {row.templateType}</div>
            <div className="text-muted">Event: {row.eventType}</div>
        </div>
    );
}

function usageCellRender(data: { data: EmailTemplateSummaryDto }) {
    const usages = data.data.usages;
    if (!usages || usages.length === 0) return <span className="text-muted">(unused)</span>;
    return (
        <div style={{ whiteSpace: "pre-wrap", lineHeight: "1.4" }}>
            {usages.map((u, i) => {
                let path = `Module: ${u.moduleName} > Task: ${u.taskName}`;
                if (u.subtaskName) path += ` > Subtask: ${u.subtaskName}`;
                return <div key={i}>{path} ({u.trigger})</div>;
            })}
        </div>
    );
}

const toggleStyle: React.CSSProperties = {
    display: "inline-flex",
    borderRadius: 4,
    overflow: "hidden",
    border: "1px solid #ddd",
};

const btnBase: React.CSSProperties = {
    padding: "4px 14px",
    cursor: "pointer",
    border: "none",
    fontSize: "0.9em",
};

export default function EmailTemplatesView({
    templates, treeNodes, loading, selectedTemplateName, dialogVisible,
    viewMode, onViewModeChange, onRowClick, onDialogHiding,
}: Props) {
    return (
        <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div className="panelTitle">
                    Email Templates
                </div>
                <div style={{ padding: '5px 5px 5px 0' }}>
                    <div style={toggleStyle}>
                        <button
                            style={{ ...btnBase, background: viewMode === "grid" ? "#337ab7" : "#f5f5f5", color: viewMode === "grid" ? "#fff" : "#333" }}
                            onClick={() => onViewModeChange("grid")}
                        >
                            By Template
                        </button>
                        <button
                            style={{ ...btnBase, background: viewMode === "tree" ? "#337ab7" : "#f5f5f5", color: viewMode === "tree" ? "#fff" : "#333" }}
                            onClick={() => onViewModeChange("tree")}
                        >
                            By Usage
                        </button>
                    </div>
                </div>
            </div>
            <hr />

            {viewMode === "grid" ? (
                <DataGrid
                    dataSource={templates}
                    keyExpr="name"
                    showBorders
                    columnAutoWidth
                    wordWrapEnabled
                    height={window.innerHeight - 250}
                    onRowClick={(e) => {
                        if (e.rowType === "data") onRowClick(e.data.name);
                    }}
                    hoverStateEnabled
                >
                    <LoadPanel enabled={loading} />
                    <Scrolling mode="virtual" />
                    <FilterRow visible />
                    <Sorting mode="single" />
                    <Column dataField="name" caption="Name" sortOrder="asc" cellRender={nameCellRender} />
                    <Column
                        caption="Used By"
                        calculateCellValue={(row: EmailTemplateSummaryDto) => formatUsages(row.usages)}
                        cellRender={usageCellRender}
                        allowSorting={false}
                    />
                </DataGrid>
            ) : (
                <EmailTemplatesTreeView
                    treeNodes={treeNodes}
                    loading={loading}
                    onTemplateClick={onRowClick}
                />
            )}

            <EmailTemplateViewerDialog
                visible={dialogVisible}
                onHiding={onDialogHiding}
                templateName={selectedTemplateName}
            />
        </>
    );
}
